import nacl from 'tweetnacl';
import util from 'tweetnacl-util';
import { keccak256 } from 'js-sha3';

/**
 * Converts a public key to an Ethereum-style address (0x format)
 * @param {Uint8Array} publicKey The public key as Uint8Array
 * @returns {string} The Ethereum-style address
 */
export const publicKeyToEthAddress = (publicKey) => {
  // Ethereum addresses are derived from the Keccak-256 hash of the public key
  // We only use the last 20 bytes of the hash (40 hex characters)
  const hash = keccak256(publicKey);
  const address = '0x' + hash.substring(hash.length - 40);
  return address;
};

/**
 * Creates a new cryptographic identity
 * @returns {Object} The identity object with keypair and display name
 */
export const createIdentity = (displayName = '') => {
  // Generate a new keypair
  const keypair = nacl.sign.keyPair();
  
  // Convert the keys to Base64 for storage
  const publicKeyBase64 = util.encodeBase64(keypair.publicKey);
  const secretKeyBase64 = util.encodeBase64(keypair.secretKey);
  
  // Generate Ethereum-style address from public key
  const ethAddress = publicKeyToEthAddress(keypair.publicKey);
  
  // Generate a short ID from the public key (first 6 characters of address without 0x prefix)
  const shortId = ethAddress.substring(2, 8);
  
  // Create the identity object
  return {
    displayName: displayName || `Anonymous_${shortId}`,
    publicKey: publicKeyBase64,
    ethAddress: ethAddress, // Add Ethereum-style address
    secretKey: secretKeyBase64,
    shortId
  };
};

/**
 * Imports an identity from secret key
 * @param {string} secretKeyBase64 The secret key in Base64 format
 * @param {string} displayName Optional display name
 * @returns {Object} The identity object
 */
export const importIdentity = (secretKeyBase64, displayName = '') => {
  try {
    // Decode the secret key
    const secretKey = util.decodeBase64(secretKeyBase64);
    
    // Extract the public key from the secret key (for ed25519, public key is the first 32 bytes)
    const keypair = nacl.sign.keyPair.fromSecretKey(secretKey);
    const publicKeyBase64 = util.encodeBase64(keypair.publicKey);
    
    // Generate Ethereum-style address from public key
    const ethAddress = publicKeyToEthAddress(keypair.publicKey);
    
    // Generate a short ID from the public key
    const shortId = ethAddress.substring(2, 8);
    
    // Create the identity object
    return {
      displayName: displayName || `Anonymous_${shortId}`,
      publicKey: publicKeyBase64,
      ethAddress: ethAddress, // Add Ethereum-style address
      secretKey: secretKeyBase64,
      shortId
    };
  } catch (error) {
    console.error('Error importing identity:', error);
    throw new Error('Invalid secret key format');
  }
};

/**
 * Signs a message with a secret key
 * @param {string} message The message to sign
 * @param {string} secretKeyBase64 The secret key in Base64 format
 * @returns {string} The signature in Base64 format
 */
export const signMessage = (message, secretKeyBase64) => {
  // Convert message to Uint8Array
  const messageUint8 = util.decodeUTF8(JSON.stringify(message));
  
  // Decode the secret key
  const secretKey = util.decodeBase64(secretKeyBase64);
  
  // Sign the message
  const signature = nacl.sign.detached(messageUint8, secretKey);
  
  // Convert the signature to Base64
  return util.encodeBase64(signature);
};

/**
 * Verifies a message signature
 * @param {string} message The original message
 * @param {string} signatureBase64 The signature in Base64 format
 * @param {string} publicKeyBase64 The public key in Base64 format
 * @returns {boolean} Whether the signature is valid
 */
export const verifySignature = (message, signatureBase64, publicKeyBase64) => {
  try {
    // Convert message to Uint8Array
    const messageUint8 = util.decodeUTF8(JSON.stringify(message));
    
    // Decode the signature and public key
    const signature = util.decodeBase64(signatureBase64);
    const publicKey = util.decodeBase64(publicKeyBase64);
    
    // Verify the signature
    return nacl.sign.detached.verify(messageUint8, signature, publicKey);
  } catch (error) {
    console.error('Error verifying signature:', error);
    return false;
  }
};

/**
 * Validates a display name
 * @param {string} displayName The display name to validate
 * @returns {boolean} Whether the display name is valid
 */
export const validateDisplayName = (displayName) => {
  // Display name must be at least 3 characters and at most 30 characters
  // It can only contain alphanumeric characters, underscores, and hyphens
  return /^[a-zA-Z0-9_-]{3,30}$/.test(displayName);
};

/**
 * Exports an identity to a JSON object
 * @param {Object} identity The identity object
 * @returns {Object} The identity object for export
 */
export const exportIdentity = (identity) => {
  return {
    type: 'decentralized-sharing-identity',
    version: '1.0',
    displayName: identity.displayName,
    publicKey: identity.publicKey,
    ethAddress: identity.ethAddress,
    secretKey: identity.secretKey,
    shortId: identity.shortId,
    createdAt: new Date().toISOString()
  };
};

/**
 * Saves an identity to localStorage
 * @param {Object} identity The identity object to save
 */
export const saveIdentityToStorage = (identity) => {
  try {
    localStorage.setItem('safety_documentation_identity', JSON.stringify(identity));
    return true;
  } catch (error) {
    console.error('Error saving identity to localStorage:', error);
    return false;
  }
};

/**
 * Loads an identity from localStorage
 * @returns {Object|null} The identity object or null if not found
 */
export const loadIdentityFromStorage = () => {
  try {
    const storedIdentity = localStorage.getItem('safety_documentation_identity');
    return storedIdentity ? JSON.parse(storedIdentity) : null;
  } catch (error) {
    console.error('Error loading identity from localStorage:', error);
    return null;
  }
};

/**
 * Clears identity from localStorage
 */
export const clearIdentityFromStorage = () => {
  try {
    localStorage.removeItem('safety_documentation_identity');
    return true;
  } catch (error) {
    console.error('Error clearing identity from localStorage:', error);
    return false;
  }
};